import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import {
  Link2, Search, RefreshCw, Copy, Send, Calendar, CheckCircle, XCircle,
  Clock, AlertTriangle, Mail, ExternalLink, Loader2, Edit, Archive, Unlock
} from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLinks = () => {
  const { getAuthHeader } = useAuth();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingLink, setEditingLink] = useState(null);
  const [newExpirationDate, setNewExpirationDate] = useState('');
  const [updatingLink, setUpdatingLink] = useState(false);
  const [sendingEmail, setSendingEmail] = useState({});

  const fetchLinks = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/approval-links`, getAuthHeader());
      setLinks(response.data);
    } catch (error) {
      console.error('Error fetching links:', error);
      toast.error('Failed to load approval links');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLinks();
  }, []);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle size={12} className="mr-1" /> Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><XCircle size={12} className="mr-1" /> Rejected</Badge>;
      case 'expired':
        return <Badge className="bg-gray-100 text-gray-700"><Clock size={12} className="mr-1" /> Expired</Badge>;
      case 'archived':
        return <Badge className="bg-gray-100 text-gray-600"><Archive size={12} className="mr-1" /> Archived</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-700"><AlertTriangle size={12} className="mr-1" /> Pending</Badge>;
    }
  };

  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard!');
  };

  const handleSendEmail = async (link) => {
    if (!link.client_email) {
      toast.error('No client email address available');
      return;
    }

    setSendingEmail(prev => ({ ...prev, [link.id]: true }));

    try {
      const response = await axios.post(
        `${API}/admin/jobs/${link.id}/send-client-approval-email`,
        {},
        getAuthHeader()
      );
      toast.success(response.data.message || `Email sent to ${link.client_email}`);
      fetchLinks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(prev => ({ ...prev, [link.id]: false }));
    }
  };

  const handleOpenEditDialog = (link) => {
    setEditingLink(link);
    // Set default expiration to 30 days from now
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + 30);
    setNewExpirationDate(futureDate.toISOString().split('T')[0]);
  };

  const handleUpdateExpiration = async () => {
    if (!editingLink || !newExpirationDate) return;

    setUpdatingLink(true);
    try {
      await axios.put(
        `${API}/admin/approval-links/${editingLink.id}`,
        { expiration_date: `${newExpirationDate}T23:59:59` },
        getAuthHeader()
      );
      toast.success('Link expiration updated');
      setEditingLink(null);
      fetchLinks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update link');
    } finally {
      setUpdatingLink(false);
    }
  };

  const handleRegenerateToken = async (link) => {
    if (!window.confirm(`Regenerate a new approval link for "${link.job_title}"? The old link will no longer work.`)) {
      return;
    }

    try {
      const response = await axios.put(
        `${API}/admin/approval-links/${link.id}`,
        { regenerate_token: true },
        getAuthHeader()
      );
      toast.success('New approval link generated');
      fetchLinks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to regenerate link');
    }
  };

  const handleExpireLink = async (link) => {
    if (!window.confirm(`Mark the approval link for "${link.job_title}" as expired?`)) {
      return;
    }

    try {
      await axios.post(
        `${API}/admin/approval-links/${link.id}/expire`,
        {},
        getAuthHeader()
      );
      toast.success('Link marked as expired');
      fetchLinks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to expire link');
    }
  };

  // Detect rows whose job is "stuck" — the client approved (so the link
  // panel shows "Approved") but the underlying job's status is still in a
  // rejection/expiration state, so the job is invisible in the Jobs
  // Management list. A one-shot "Unstick" admin action fixes the row
  // forward without poking Mongo by hand.
  const STUCK_JOB_STATUSES = ['client_rejected', 'rejected', 'expired'];
  const isLinkStuck = (link) =>
    link.status === 'approved'
    && link.job_status
    && STUCK_JOB_STATUSES.includes(link.job_status);

  const handleUnstickJob = async (link) => {
    if (!window.confirm(
      `Unstick "${link.job_title}"? The job's status will move to ` +
      `${link.admin_approved ? '"open"' : '"pending_approval"'} and the ` +
      `stale rejection metadata will be cleared.`
    )) return;
    try {
      const res = await axios.post(
        `${API}/admin/approval-links/${link.id}/unstick`,
        {},
        getAuthHeader()
      );
      toast.success(`Unstuck — status moved to ${res.data?.new_status || 'open'}`);
      fetchLinks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unstick job');
    }
  };

  // Filter links
  const filteredLinks = links.filter(link => {
    const matchesSearch = 
      link.job_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      link.client?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      link.client_email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || link.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Group links by status
  const groupedLinks = {
    pending: filteredLinks.filter(l => l.status === 'pending'),
    approved: filteredLinks.filter(l => l.status === 'approved'),
    expired: filteredLinks.filter(l => l.status === 'expired'),
    rejected: filteredLinks.filter(l => l.status === 'rejected'),
    archived: filteredLinks.filter(l => l.status === 'archived')
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Link2 className="text-purple-600" />
            Client Approval Links
          </h1>
          <p className="text-gray-500 mt-1">Manage and track all client approval links</p>
        </div>
        <Button onClick={fetchLinks} variant="outline" className="gap-2">
          <RefreshCw size={16} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <Input
              placeholder="Search by job, client, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border rounded-md px-3 py-2 bg-white text-sm"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="expired">Expired</option>
            <option value="rejected">Rejected</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="p-4 text-center">
          <p className="text-2xl font-bold text-amber-600">{groupedLinks.pending.length}</p>
          <p className="text-sm text-gray-500">Pending</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{groupedLinks.approved.length}</p>
          <p className="text-sm text-gray-500">Approved</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-2xl font-bold text-gray-600">{groupedLinks.expired.length}</p>
          <p className="text-sm text-gray-500">Expired</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-2xl font-bold text-red-600">{groupedLinks.rejected.length}</p>
          <p className="text-sm text-gray-500">Rejected</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-2xl font-bold text-gray-500">{groupedLinks.archived.length}</p>
          <p className="text-sm text-gray-500">Archived</p>
        </Card>
      </div>

      {/* Links Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Job</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Created</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Expires</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredLinks.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    No approval links found
                  </td>
                </tr>
              ) : (
                filteredLinks.map(link => (
                  <tr key={link.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900 truncate max-w-[200px]" title={link.job_title}>
                          {link.job_title}
                        </p>
                        <p className="text-xs text-gray-500">{link.deliveries?.toLocaleString()} deliveries</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm text-gray-900">{link.client}</p>
                        {link.client_contact_name && (
                          <p className="text-xs text-gray-500">{link.client_contact_name}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(link.status)}
                    </td>
                    <td className="px-4 py-3">
                      {link.client_email ? (
                        <div>
                          <p className="text-sm text-gray-900 truncate max-w-[150px]" title={link.client_email}>
                            {link.client_email}
                          </p>
                          {link.email_sent && (
                            <p className="text-xs text-green-600 flex items-center gap-1">
                              <CheckCircle size={10} /> Sent {formatDate(link.email_sent_at)}
                            </p>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">No email</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatDate(link.created_at)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {link.expiration_date ? (
                        <span className={new Date(link.expiration_date) < new Date() ? 'text-red-600' : 'text-gray-500'}>
                          {formatDate(link.expiration_date)}
                        </span>
                      ) : (
                        <span className="text-gray-400">No expiration</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {/* Copy Link */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopyLink(link.url)}
                          title="Copy link"
                          className="text-gray-500 hover:text-gray-700"
                        >
                          <Copy size={16} />
                        </Button>
                        
                        {/* Open Link */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(link.url, '_blank')}
                          title="Open link"
                          className="text-blue-500 hover:text-blue-700"
                        >
                          <ExternalLink size={16} />
                        </Button>
                        
                        {/* Send Email */}
                        {link.client_email && link.status === 'pending' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleSendEmail(link)}
                            disabled={sendingEmail[link.id]}
                            title={link.email_sent ? 'Resend email' : 'Send email'}
                            className="text-purple-500 hover:text-purple-700"
                          >
                            {sendingEmail[link.id] ? (
                              <Loader2 size={16} className="animate-spin" />
                            ) : (
                              <Mail size={16} />
                            )}
                          </Button>
                        )}
                        
                        {/* Edit Expiration */}
                        {(link.status === 'expired' || link.status === 'pending') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenEditDialog(link)}
                            title="Edit expiration"
                            className="text-amber-500 hover:text-amber-700"
                          >
                            <Calendar size={16} />
                          </Button>
                        )}
                        
                        {/* Regenerate Token */}
                        {link.status !== 'approved' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRegenerateToken(link)}
                            title="Generate new link"
                            className="text-green-500 hover:text-green-700"
                          >
                            <RefreshCw size={16} />
                          </Button>
                        )}

                        {/* Unstick — only when the row is in the buggy
                            "client approved but job status still rejected/
                            expired" state. Click moves job.status to
                            open/pending_approval and clears the stale
                            rejection flags so the row reappears in Jobs
                            Management. */}
                        {isLinkStuck(link) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleUnstickJob(link)}
                            title={`Unstick — job is in '${link.job_status}' despite being client-approved`}
                            className="text-amber-600 hover:text-amber-800"
                            data-testid={`unstick-job-${link.id}`}
                          >
                            <Unlock size={16} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Edit Expiration Dialog */}
      <Dialog open={!!editingLink} onOpenChange={() => setEditingLink(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Link Expiration</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-1">Job</p>
              <p className="text-gray-900">{editingLink?.job_title}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700 mb-1">Current Status</p>
              {editingLink && getStatusBadge(editingLink.status)}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">New Expiration Date</label>
              <Input
                type="date"
                value={newExpirationDate}
                onChange={(e) => setNewExpirationDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
              <p className="text-xs text-gray-500 mt-1">
                Setting a future date will reactivate expired links
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingLink(null)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateExpiration} 
              disabled={updatingLink || !newExpirationDate}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {updatingLink ? (
                <><Loader2 size={16} className="mr-2 animate-spin" /> Updating...</>
              ) : (
                'Update Expiration'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLinks;
